import React from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { Person as PersonIcon } from "@mui/icons-material";
import { hasSplitPayment } from "../utils/formatters";
import LessonLocation from "../components/LessonLocation";

const AlreadySignedUpDialog = ({ open, onClose, header, dateLine, signingOut, onSignOut, lessonData, myLesson }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isSplitPayment = hasSplitPayment(lessonData);
  // A whole duo/trio is reserved and cancelled as a whole by the booker only;
  // a single spot is the user's own seat and is cancelled like a regular lesson.
  const bookedBy = myLesson?.booked_by;
  const duoSpot = Boolean(myLesson?.is_duo) && Boolean(myLesson?.single_spot);
  const isDuo = Boolean(myLesson?.is_duo) && !duoSpot;
  const duoGuest = isDuo && Boolean(bookedBy);
  const canSignOut = !isSplitPayment && !duoGuest;
  
  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      fullWidth 
      maxWidth="sm"
      PaperProps={{
        sx: {
          borderRadius: 2,
          m: isMobile ? 2 : 3,
        }
      }}
    >
      <DialogTitle sx={{ 
        textAlign: 'center', 
        pb: 1,
        pt: 3,
      }}>
        <Typography variant={isMobile ? "h6" : "h5"} fontWeight="600" gutterBottom>
          {header}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {dateLine}
        </Typography>
        {lessonData?.trainer?.full_name && (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5, mt: 0.5 }}>
            <PersonIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
            <Typography variant="body2" color="text.secondary" fontWeight="500">
              {lessonData.trainer.full_name}
            </Typography>
          </Box>
        )}
        {lessonData?.lesson_type?.location && (
          <LessonLocation location={lessonData.lesson_type.location} />
        )}
      </DialogTitle>
      
      <DialogContent sx={{ pt: 2, pb: 3 }}>
        <Stack spacing={2}>
          <Card 
            elevation={0} 
            sx={{ 
              backgroundColor: '#e3f2fd',
              border: '2px solid #2196f3',
              borderRadius: 2
            }}
          >
            <CardContent>
              <Stack direction="row" spacing={2} alignItems="center">
                <Box sx={{ fontSize: '2rem' }}>✅</Box>
                <Box>
                  <Typography variant="body1" fontWeight="500">
                    Jesteś już zapisany/a na te zajęcia
                  </Typography>
                  {bookedBy && (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }} data-testid="already-booked-by">
                      Zapisane przez: {bookedBy.full_name}
                    </Typography>
                  )}
                  {canSignOut && (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      {isDuo
                        ? "Czy chcesz anulować całą rezerwację? Wszyscy zapisani z Tobą zostaną wypisani."
                        : "Czy chcesz się wypisać?"}
                    </Typography>
                  )}
                </Box>
              </Stack>
            </CardContent>
          </Card>
          
          {duoSpot && canSignOut && !lessonData?.is_free && !lessonData?.payment_on_site && (
            <Alert severity="info" sx={{ borderRadius: 2 }} data-testid="duo-spot-signout-info">
              <Typography variant="body2">
                Jeśli miejsce było opłacone, po wypisaniu dostaniesz karnet „Odrabianie miejsca duo/trio”.
              </Typography>
            </Alert>
          )}

          {duoGuest && (
            <Alert severity="info" sx={{ borderRadius: 2 }} data-testid="duo-guest-info">
              <Typography variant="body2">
                To rezerwacja całych zajęć duo/trio. Może ją anulować tylko osoba, która ją zrobiła
                ({bookedBy.full_name}).
              </Typography>
            </Alert>
          )}

          {isSplitPayment && (
            <Alert severity="error" icon="🚫" sx={{ borderRadius: 2 }}>
              <Typography variant="body2" fontWeight="600" gutterBottom>
                Nie możesz wypisać się z tych zajęć
              </Typography>
              <Typography variant="caption">
                Te zajęcia mają płatność podzieloną. Zadatek online nie podlega zwrotowi, 
                dlatego wypisanie się nie jest możliwe.
              </Typography>
            </Alert>
          )}
        </Stack>
      </DialogContent>
      
      <DialogActions sx={{ 
        px: 3, 
        pb: 3, 
        gap: 1,
        flexDirection: isMobile ? 'column' : 'row',
        justifyContent: canSignOut ? 'space-between' : 'center'
      }}>
        {!canSignOut ? (
          <Button 
            onClick={onClose} 
            variant="contained"
            color="primary"
            fullWidth={isMobile}
            sx={{ minHeight: 44 }}
          >
            OK
          </Button>
        ) : (
          <>
            <Button 
              onClick={onClose} 
              variant="outlined"
              fullWidth={isMobile}
              sx={{ minHeight: 44 }}
            >
              Anuluj
            </Button>
            <Button
              onClick={onSignOut}
              color="error"
              variant="contained"
              disabled={signingOut}
              fullWidth={isMobile}
              sx={{ minHeight: 44 }}
            >
              {signingOut ? <CircularProgress size={20} color="inherit" /> : isDuo ? "Anuluj rezerwację" : "Wypisz się"}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default AlreadySignedUpDialog;
